import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";
import { PDFDocument } from "pdf-lib";

// Public API
const downloadReport = async (data, outputType, reportTitle) => {
  try {
    const ts = new Date().toISOString().replace(/[:.-]/g, "");
    const baseName = `report_${ts}`;

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error("No data to export");
    }

    const isCsv = Number(outputType) === 1;
    const isPdf = Number(outputType) === 2;
    const isAllExpenses = String(reportTitle || "")
      .toLowerCase()
      .includes("all expenses");

    const isReimbursable = String(reportTitle || "")
      .toLowerCase()
      .includes("reimbursable");

    const shouldAppendReceipts = isAllExpenses || isReimbursable;

    if (isCsv) {
      const blob = buildCsvBlob(data, reportTitle);
      saveAs(blob, `${baseName}.csv`);
      return;
    }

    if (isPdf) {
      // Build the summary PDF bytes first
      const summaryBytes = await buildSummaryPdfBytes({
        data,
        reportTitle,
        hideReceiptField: true,
        isReimbursable
      });

      if (shouldAppendReceipts) {
        // Append receipts into the same PDF
        const finalBytes = await appendReceiptsToSummary(summaryBytes, data);
        const blob = new Blob([finalBytes], { type: "application/pdf" });
        saveAs(blob, `${baseName}.pdf`);
      } else {
        // Summary only
        const blob = new Blob([summaryBytes], { type: "application/pdf" });
        saveAs(blob, `${baseName}.pdf`);
      }
      return;
    }

    throw new Error("Invalid output type selected");
  } catch (err) {
    console.error("Error generating report:", err);
    alert("An error occurred while generating the report.");
  }
};

export default downloadReport;

/* ------------------------- Helpers ------------------------- */

function capitalizeColumnName(columnName) {
  return String(columnName)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatCsvValue(v) {
  if (v == null) return "";
  const s = String(v);
  // Quote if contains comma, quote or newline
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

function buildCsvBlob(data, reportTitle) {
  // Hide the raw receipt URL column
  const allKeys = Object.keys(data[0] || {});
  const headers = allKeys.filter((k) => k.toLowerCase() !== "receipt");

  let csv = `${reportTitle || "Report"}\n`;
  csv += `${headers.join(",")}\n`;
  for (const row of data) {
    csv += headers.map((h) => formatCsvValue(row[h])).join(",") + "\n";
  }
  return new Blob([csv], { type: "text/csv;charset=utf-8;" });
}

async function buildSummaryPdfBytes({ data, reportTitle, hideReceiptField, isReimbursable }) {
  const doc = new jsPDF({ orientation: "landscape" });

  // ---- Header (logo + company details + title + date) ----
  const logo =
    "data:image/png;base64,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";
  const companyName = "Kuboid Ltd";
  const address = "2 Swallow Court, Kettering Venture Park, Kettering, NN15 6XX";
  const phone = "Phone: +44 (0) 1933 222 535";
  const title = `Expense Report - ${reportTitle || ""}`;
  const dateTxt = "Date: " + new Date().toLocaleDateString();

  doc.addImage(logo, "PNG", 10, 10, 15, 15);
  const detailsStartY = 30;
  doc.setFontSize(12);
  doc.text(companyName, 10, detailsStartY);
  doc.setFontSize(10);
  doc.text(address, 10, detailsStartY + 6);
  doc.text(phone, 10, detailsStartY + 12);

  const titleY = detailsStartY + 30;
  doc.setFontSize(16);
  doc.text(title, 148.5, titleY, { align: "center" });

  doc.setFontSize(10);
  doc.text(dateTxt, 287, 10, { align: "right" });

  let currentY = 65;

  if (isReimbursable) {
    // Add heading for summary table
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Reimbursable Expenses Summary", 10, currentY);
    currentY += 8; // Adjust Y position for spacing after heading

    const summaryHeaders = ["User ID", "First Name", "Last Name", "Total Amount"];
    const summaryBody = data.reduce((acc, item) => {
      const existing = acc.find(row => row[0] === item.user_id);
      if (existing) {
        existing[3] = (parseFloat(existing[3]) + parseFloat(item.total_amount)).toFixed(2);
      } else {
        acc.push([
          item.user_id,
          item.firstname,
          item.lastname,
          parseFloat(item.total_amount).toFixed(2)
        ]);
      }
      return acc;
    }, []);

    doc.autoTable({
      startY: currentY,
      head: [summaryHeaders],
      body: summaryBody,
      theme: "striped",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [240, 240, 240], textColor: 20 },
    });
    currentY = doc.lastAutoTable.finalY + 10; // Update Y position after summary table
  }

  // ---- Detailed Table Heading ----
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Detailed Expense Report", 10, currentY);
  currentY += 8; // Adjust Y position for spacing after heading

  // ---- Detailed Table (hide raw 'receipt' field, keep your own status column intact) ----
  const allKeys = Object.keys(data[0] || {});
  const displayKeys = hideReceiptField
    ? allKeys.filter((k) => k.toLowerCase() !== "receipt")
    : allKeys;

  const headers = displayKeys.map(capitalizeColumnName);
  const body = data.map((item) => displayKeys.map((k) => safeCell(item[k])));

  doc.autoTable({
    startY: currentY,
    head: [headers],
    body,
    theme: "striped",
    styles: { fontSize: 9 },
    headStyles: { fillColor: [240, 240, 240], textColor: 20 },
  });

  return doc.output("arraybuffer");
}

function safeCell(v) {
  if (v == null) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function extractReceiptUrl(item) {
  const r = item?.receipt;
  if (!r) return "";
  if (typeof r === "string") return r.trim();
  if (r && typeof r === "object" && typeof r.url === "string") return r.url.trim();
  return "";
}

async function appendReceiptsToSummary(summaryBytes, data) {
  const finalDoc = await PDFDocument.create();

  // Copy summary pages first
  const summaryDoc = await PDFDocument.load(summaryBytes);
  const summaryPages = await finalDoc.copyPages(
    summaryDoc,
    summaryDoc.getPageIndices()
  );
  for (const p of summaryPages) finalDoc.addPage(p);

  // Then append receipts (image pages or merged PDFs)
  for (const item of data) {
    const url = extractReceiptUrl(item);
    if (!url) continue;

    try {
      const { bytes, type } = await fetchAsArrayBuffer(url);
      if (!bytes) continue;

      if (type.includes("application/pdf") || url.toLowerCase().endsWith(".pdf")) {
        const rDoc = await PDFDocument.load(bytes);
        const pages = await finalDoc.copyPages(rDoc, rDoc.getPageIndices());
        pages.forEach((pg) => finalDoc.addPage(pg));
      } else if (type.startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(url)) {
        await addImageReceiptPage(finalDoc, bytes);
      } else {
        console.warn("Unsupported receipt type:", type, url);
      }
    } catch (e) {
      console.error("Failed to append receipt:", url, e);
      // continue to next
    }
  }

  return finalDoc.save();
}

async function fetchAsArrayBuffer(url) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const type = (res.headers.get("content-type") || "").toLowerCase();
    const bytes = await res.arrayBuffer();
    return { bytes, type };
  } catch (e) {
    console.error("fetch failed:", url, e);
    return { bytes: null, type: "" };
  }
}

// Add an image receipt (no extra header — you already show status in the table)
async function addImageReceiptPage(pdfDoc, imageBytes) {
  // Use the first page size of existing doc (summary) if available; else A4 portrait
  const basePage = pdfDoc.getPageCount() ? pdfDoc.getPage(0) : null;
  const [pw, ph] = basePage
    ? [basePage.getWidth(), basePage.getHeight()]
    : [595.28, 841.89]; // A4 portrait

  const page = pdfDoc.addPage([pw, ph]);

  // Try PNG first; fallback to JPG if parsing fails
  let img;
  try {
    img = await pdfDoc.embedPng(imageBytes);
  } catch {
    img = await pdfDoc.embedJpg(imageBytes);
  }

  const margin = 36; // ~0.5 in
  const maxW = pw - margin * 2;
  const maxH = ph - margin * 2;
  const iw = img.width;
  const ih = img.height;
  const scale = Math.min(maxW / iw, maxH / ih, 1);
  const w = iw * scale;
  const h = ih * scale;
  const x = (pw - w) / 2;
  const y = (ph - h) / 2;

  page.drawImage(img, { x, y, width: w, height: h });
}